/**
 * Deterministic, multilingual budget parsing — a PURE, dependency-free module
 * (safe to import from the search-bar client bundle, server routes, and tests).
 *
 * WHY THIS EXISTS
 * The conversation AI deliberately STRIPS price words from `searchQuery`
 * ("Pricing is handled separately") and is forbidden from asking about price —
 * so a query like "ordinateur pour élève au cégep, Windows, moins de 1500$"
 * used to lose its one hard constraint before any search ran, and pages built
 * from it carried $4,000+ picks. This parser recovers the stated ceiling the
 * instant the user submits, before any AI token arrives.
 *
 * PRECISION FIRST: never mint a budget the user didn't state. A number counts
 * only when it carries a currency signal ($ € £, "dollars", "CAD"…) OR sits
 * next to an explicit budget marker ("moins de", "under", "max", "budget de"…).
 * Spec-glued numbers ("RTX 5060", "Windows 11", "128gb", "55 pouces") and bare
 * years are rejected by construction. When anything is ambiguous, return null —
 * the conversation AI is the backstop for phrasing this parser can't prove.
 */

export interface ParsedBudget {
  /** Ceiling in major currency units. */
  max: number;
  /** Present only for an explicit range ("entre 800 et 1200$"). */
  min?: number;
  /** ISO 4217. An ambiguous "$" resolves to `opts.localeCurrency`. */
  currency: string;
  /** The matched substring, verbatim — audit trail + AI context. */
  raw: string;
}

/** Sanity clamp: below 5 it's noise, above 100k it's not consumer shopping. */
const MIN_BUDGET = 5;
const MAX_BUDGET = 100_000;
/** A stated range wider than 50x is a misparse, not a budget. */
const MAX_RANGE_SPREAD = 50;

/** Explicit currency symbols → ISO code (null = resolve by locale). */
const SYMBOL_CURRENCY: Record<string, string | null> = {
  $: null, // ambiguous: CAD on fr-CA/en-CA, USD on en-US, AUD on en-AU…
  "€": "EUR",
  "£": "GBP",
};

/** Currency words/codes → ISO code (null = locale default, e.g. "dollars"). */
const WORD_CURRENCY: Record<string, string | null> = {
  cad: "CAD",
  usd: "USD",
  eur: "EUR",
  gbp: "GBP",
  aud: "AUD",
  chf: "CHF",
  mxn: "MXN",
  euro: "EUR",
  euros: "EUR",
  pound: "GBP",
  pounds: "GBP",
  livre: "GBP",
  livres: "GBP",
  franc: "CHF",
  francs: "CHF",
  peso: "MXN",
  pesos: "MXN",
  dollar: null,
  dollars: null,
  piasse: null, // Québec French slang for dollar
  piasses: null,
  buck: null,
  bucks: null,
};

/**
 * Ceiling markers BEFORE the amount ("moins de 1500$"). Checked against the
 * folded text immediately preceding the number (longest first). fr/en/es/de.
 */
const MARKERS_BEFORE_CEILING = [
  "moins de",
  "moins que",
  "en dessous de",
  "en bas de",
  "au maximum",
  "sous les",
  "sous la barre de",
  "jusqu a",
  "budget de",
  "budget d environ",
  "budget max de",
  "budget maximum de",
  "un budget de",
  "pour un budget de",
  "less than",
  "under",
  "below",
  "up to",
  "at most",
  "no more than",
  "not more than",
  "max of",
  "maximum of",
  "a maximum of",
  "budget of",
  "a budget of",
  "capped at",
  "menos de",
  "por menos de",
  "por debajo de",
  "hasta",
  "un presupuesto de",
  "presupuesto de",
  "unter",
  "weniger als",
  "hochstens",
  "bis zu",
  "bis",
  "maximal",
  "budget von",
];

/** Ceiling markers AFTER the amount ("1500$ max", "300 ou moins"). */
const MARKERS_AFTER_CEILING = [
  "max",
  "maxi",
  "maximum",
  "tops",
  "ou moins",
  "or less",
  "o menos",
  "oder weniger",
  "grand max",
  "au max",
  "at most",
];

/** Approximation markers — treated as a soft ceiling with 10% headroom. */
const MARKERS_APPROX = [
  "environ",
  "aux alentours de",
  "autour de",
  "a peu pres",
  "around",
  "about",
  "roughly",
  "approximately",
  "aprox",
  "aproximadamente",
  "alrededor de",
  "etwa",
  "ungefahr",
  "circa",
  "~",
];

/** Range connectors for "entre X et Y" / "between X and Y" / "de X à Y". */
const RANGE_OPENERS = ["entre", "between", "zwischen"];
const RANGE_CONNECTORS = ["et", "and", "y", "und", "a", "à", "to"];

/**
 * Spec/model tokens: a number directly following one of these is a product
 * attribute, never money ("rtx 5060", "windows 11", "iphone 15", "i7 13700k").
 * Matched on the folded token immediately before the number.
 */
const SPEC_PREFIX_TOKENS = new Set([
  "rtx", "gtx", "rx", "gt", "mx", "arc",
  "i3", "i5", "i7", "i9", "ryzen", "core", "ultra",
  "m1", "m2", "m3", "m4",
  "windows", "android", "ios", "macos",
  "iphone", "galaxy", "pixel", "ipad", "ps", "playstation", "xbox",
  "gen", "ddr", "lpddr", "pcie", "usb", "hdmi", "wifi", "wi-fi",
  "wh", "xm", "qc", "sm", "gtx", "version", "v",
  "top", "best", "meilleurs", "meilleur", // "top 5", "best 10"
]);

/**
 * Unit suffixes glued to (or one space after) a number that make it a spec,
 * not money: "128gb", "55 pouces", "750w", "3l", "2000mah", "144hz".
 */
const SPEC_SUFFIX_RE =
  /^(?:gb|tb|mb|go|to|mo|hz|khz|mhz|ghz|mah|wh|mp|mpx|nits?|dpi|ppi|fps|w|kw|v|a|ah|l|ml|cl|g|kg|mg|lb|lbs|oz|mm|cm|m|km|in|ft|po|pouces?|inch(?:es)?|ans?|years?|jours?|days?|hours?|heures?|h|min|k)\b/;

/** Fold: lowercase + strip combining accents (é→e) — mirrors foldQuantityText. */
const fold = (text: string): string =>
  text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/**
 * Parse one numeral string ("1500", "1 500", "1,500", "1.500", "1500.50",
 * "1,5" fr-decimal, "1.5k"/"2k") into a number, or null.
 */
function parseAmount(rawNumber: string, kSuffix: boolean): number | null {
  // normalize all space-like group separators away (regular, NBSP, narrow NBSP)
  let s = rawNumber.replace(/[\s  ]/g, "");
  if (!s) return null;

  const lastComma = s.lastIndexOf(",");
  const lastDot = s.lastIndexOf(".");
  const sep = Math.max(lastComma, lastDot);
  if (lastComma !== -1 && lastDot !== -1) {
    // both present: the LAST one is the decimal separator, the other groups
    const decimals = s.length - sep - 1;
    const intPart = s.slice(0, sep).replace(/[.,]/g, "");
    s = decimals <= 2 ? `${intPart}.${s.slice(sep + 1)}` : intPart + s.slice(sep + 1);
  } else if (sep !== -1) {
    const tail = s.length - sep - 1;
    // exactly 3 digits after a single separator = thousands ("1,500" / "1.500")
    if (tail === 3) s = s.replace(/[.,]/g, "");
    else s = s.replace(",", ".");
  }

  const value = Number.parseFloat(s);
  if (!Number.isFinite(value)) return null;
  return kSuffix ? value * 1000 : value;
}

interface AmountMatch {
  value: number;
  /** True when an explicit currency symbol/word adjoins the number. */
  hasCurrency: boolean;
  /** ISO code when the currency signal was unambiguous, else null. */
  currency: string | null;
  /** True when the value came from a k-suffix ("2k" → 2000) — exempt from the year guard. */
  kExpanded: boolean;
  start: number;
  end: number;
}

/**
 * Scan the ORIGINAL text for candidate monetary amounts: a numeral with an
 * optional adjoining currency signal (symbol before/after, word/code after).
 * Spec-glued numbers are rejected here.
 */
function scanAmounts(original: string, folded: string): AmountMatch[] {
  const out: AmountMatch[] = [];
  // numeral with optional space-grouped thousands and optional k-suffix
  const numRe =
    /(\d{1,3}(?:[   ][0-9]{3})+|\d+(?:[.,]\d+)*)\s?(k\b)?/gi;
  let m: RegExpExecArray | null;
  while ((m = numRe.exec(original)) !== null) {
    const numStart = m.index;
    let numEnd = m.index + m[0].length;
    let kSuffix = Boolean(m[2]);

    // --- spec guards --------------------------------------------------------
    // token immediately BEFORE the number ("rtx 5060", "windows 11")
    const before = folded.slice(0, numStart).trimEnd();
    const prevToken = before.split(/[\s,;:!?()]+/).pop() ?? "";
    if (SPEC_PREFIX_TOKENS.has(prevToken)) continue;
    // letter glued directly to the number's left ("xm5", "a1500")
    if (/[a-z0-9]$/i.test(original.slice(0, numStart))) continue;
    // unit glued or one space after ("128gb", "55 pouces") — but 'k' money suffix already consumed
    const afterRaw = fold(original.slice(numEnd)).replace(/^[\s  ]/, "");
    if (!kSuffix && SPEC_SUFFIX_RE.test(afterRaw)) {
      // "2k" money vs "4k" resolution: only treat trailing k as money when the
      // number is ≥ 100 or has a decimal — "1.5k budget" yes, "4k monitor" no.
      const kMatch = /^k\b/.test(afterRaw);
      const numericValue = parseAmount(m[1], false);
      if (kMatch && numericValue !== null && (numericValue >= 100 || /[.,]/.test(m[1]))) {
        kSuffix = true;
        numEnd = numEnd + (original.slice(numEnd).match(/^\s?k/i)?.[0].length ?? 0);
      } else {
        continue;
      }
    }

    const value = parseAmount(m[1], kSuffix);
    if (value === null) continue;

    // --- currency signal ----------------------------------------------------
    let hasCurrency = false;
    let currency: string | null = null;
    let start = numStart;
    let end = numEnd;

    // symbol BEFORE ("$1,500", "$ 1500") — allow one space
    const beforeSym = original.slice(Math.max(0, numStart - 2), numStart);
    const symBefore = beforeSym.match(/([$€£])\s?$/);
    if (symBefore) {
      hasCurrency = true;
      currency = SYMBOL_CURRENCY[symBefore[1]] ?? null;
      start = numStart - symBefore[0].length;
    }
    // symbol AFTER ("1500$", "1 500 $") — allow one space (incl. NBSP)
    const afterSlice = original.slice(numEnd, numEnd + 3);
    const symAfter = afterSlice.match(/^[\s  ]?([$€£])/);
    if (!hasCurrency && symAfter) {
      hasCurrency = true;
      currency = SYMBOL_CURRENCY[symAfter[1]] ?? null;
      end = numEnd + symAfter[0].length;
    }
    // word/code AFTER ("300 dollars", "1500 CAD")
    if (!hasCurrency) {
      const wordAfter = fold(original.slice(numEnd)).match(/^[\s  ]?([a-z]+)/);
      if (wordAfter && wordAfter[1] in WORD_CURRENCY) {
        hasCurrency = true;
        currency = WORD_CURRENCY[wordAfter[1]] ?? null;
        end = numEnd + wordAfter[0].length;
      }
    }

    out.push({ value, hasCurrency, currency, kExpanded: kSuffix, start, end });
  }
  return out;
}

/** Does one of `markers` end exactly at `pos` in the folded text? Longest first. */
function markerEndingAt(folded: string, pos: number, markers: string[]): string | null {
  const before = folded.slice(0, pos).replace(/[\s  ]+$/, "");
  for (const marker of [...markers].sort((a, b) => b.length - a.length)) {
    if (before.endsWith(marker)) {
      // whole-word boundary on the left of the marker
      const at = before.length - marker.length;
      if (at === 0 || /[\s,;:!?()]/.test(before[at - 1])) return marker;
    }
  }
  return null;
}

/** Does one of `markers` start right after `pos`? */
function markerStartingAt(folded: string, pos: number, markers: string[]): string | null {
  const after = folded.slice(pos).replace(/^[\s  ,]+/, "");
  for (const marker of [...markers].sort((a, b) => b.length - a.length)) {
    if (after.startsWith(marker)) {
      const boundary = after[marker.length];
      if (boundary === undefined || /[\s,;:.!?()]/.test(boundary)) return marker;
    }
  }
  return null;
}

/**
 * Parse a user's free-text query for a stated budget.
 * Returns null when no confident budget statement exists.
 */
export function parseBudgetFromText(
  text: string,
  opts: { localeCurrency: string },
): ParsedBudget | null {
  if (!text || typeof text !== "string" || text.length > 2000) return null;
  const original = text;
  const folded = fold(text);

  const amounts = scanAmounts(original, folded);
  if (amounts.length === 0) return null;

  const clampOk = (v: number) => v >= MIN_BUDGET && v <= MAX_BUDGET;
  // A markerless/currencyless 4-digit value in the year range is a model year
  // ("cars under 2020"). k-expanded values ("2k") and explicit budget-word
  // markers ("budget de 2000") are exempt — those are unambiguously money.
  const looksLikeBareYear = (a: AmountMatch, marker: string | null) =>
    !a.hasCurrency &&
    !a.kExpanded &&
    !(marker && /budget|presupuesto/.test(marker)) &&
    Number.isInteger(a.value) &&
    a.value >= 1900 &&
    a.value <= 2099;

  // --- explicit range: "entre X et Y" / "between X and Y" / "$800-1200" ------
  for (let i = 0; i < amounts.length - 1; i++) {
    const a = amounts[i];
    const b = amounts[i + 1];
    if (!clampOk(a.value) || !clampOk(b.value)) continue;
    const between = folded.slice(a.end, b.start);
    const connector = between.replace(/[\s  ]/g, "");
    const wordConnector = between.trim().split(/[\s  ]+/).filter(Boolean);
    const isDashRange = /^[-–—]$/.test(connector);
    const isWordRange =
      wordConnector.length === 1 && RANGE_CONNECTORS.includes(wordConnector[0]);
    if (!isDashRange && !isWordRange) continue;
    // word-connector ranges need an opener ("entre", "between") OR currency on a side
    const hasOpener = markerEndingAt(folded, a.start, RANGE_OPENERS) !== null;
    const hasCurrency = a.hasCurrency || b.hasCurrency;
    if (isWordRange && !hasOpener && !hasCurrency) continue;
    if (isDashRange && !hasCurrency) continue;
    let [min, max] = [a.value, b.value];
    if (min > max) [min, max] = [max, min];
    if (max / Math.max(min, 1) > MAX_RANGE_SPREAD) continue;
    return {
      min,
      max,
      currency: a.currency ?? b.currency ?? opts.localeCurrency,
      raw: original.slice(Math.min(a.start, hasOpener ? a.start : a.start), b.end).trim(),
    };
  }

  // --- single ceiling --------------------------------------------------------
  // Preference order: currency+marker > currency-only > marker-only; first wins ties.
  let best: { a: AmountMatch; score: number; approx: boolean; markerStart: number } | null =
    null;
  for (const a of amounts) {
    if (!clampOk(a.value)) continue;
    const beforeCeiling = markerEndingAt(folded, a.start, MARKERS_BEFORE_CEILING);
    if (looksLikeBareYear(a, beforeCeiling)) continue;
    const afterCeiling = markerStartingAt(folded, a.end, MARKERS_AFTER_CEILING);
    const approx = markerEndingAt(folded, a.start, MARKERS_APPROX) !== null;
    const hasMarker = Boolean(beforeCeiling || afterCeiling || approx);
    if (!a.hasCurrency && !hasMarker) continue;
    const score = (a.hasCurrency ? 2 : 0) + (hasMarker ? 1 : 0);
    if (!best || score > best.score) {
      const markerStart = beforeCeiling
        ? folded.slice(0, a.start).replace(/[\s  ]+$/, "").length -
          beforeCeiling.length
        : a.start;
      best = { a, score, approx, markerStart };
    }
  }
  if (!best) return null;

  // A bare currency-signal number with no marker anywhere ("a $500 gift card"?)
  // is still accepted: stating an amount with a currency in a product search is
  // a price signal — the conversation AI refines intent. But require it to be
  // the ONLY amount in the text to avoid picking one of several prices.
  if (best.score === 2) {
    const monetary = amounts.filter((x) => x.hasCurrency);
    if (monetary.length > 1) return null;
  }

  const afterMarker = markerStartingAt(folded, best.a.end, MARKERS_AFTER_CEILING);
  const rawEnd = afterMarker
    ? folded.indexOf(afterMarker, best.a.end) + afterMarker.length
    : best.a.end;
  const max = best.approx ? Math.round(best.a.value * 1.1) : best.a.value;
  return {
    max,
    currency: best.a.currency ?? opts.localeCurrency,
    raw: original.slice(best.markerStart, rawEnd).trim(),
  };
}
